import { Spinner } from '@Common/Spinner';
import { classNames } from 'helpers/validation';
import { useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useGetPrices, useResumeSubscription } from 'store/subscription/actions';
import { SubscriptionCustomerType } from 'store/subscription/types';

import { CancelSubscriptionModal } from '../ChangeModals';
import { UpgradeSubscriptionModal } from '../ChangeModals/UpgradeSubscriptionModal';
import { PaymentBadge } from './PaymentBadge';

interface SubscriptionCardProps {
  subscription: SubscriptionCustomerType;
}

const subscriptionTypeMap = {
  MENU: 'Menu subscription',
  POS: 'POS subscription',
};

export const SubscriptionCard = ({ subscription }: SubscriptionCardProps) => {
  const { isPending: isPricePending } = useGetPrices();
  const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
  const { mutate: resumeSubscription, isPending: isResumePending } =
    useResumeSubscription();
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false);
  const isSubscriptionOnCancelPeriod =
    subscription?.cancel_at_period_end &&
    subscription.status !== 'Cancel' &&
    subscription.status !== 'PastDue';

  return (
    <>
      <div className="flex flex-col items-start rounded-md border border-gray-300 p-3 w-[300px]">
        <p className="text-primary font-semibold mb-4">
          {
            subscriptionTypeMap[
              subscription.product_type as keyof typeof subscriptionTypeMap
            ]
          }
        </p>

        <p className="text-sm font-semibold mb-2">
          <FormattedMessage id="renewalDay" />:{' '}
          {subscription.next_payment_date ? (
            <FormattedDate
              value={subscription.next_payment_date * 1000}
              year="numeric"
              month="2-digit"
              day="2-digit"
            />
          ) : (
            '-'
          )}
        </p>

        <p className="text-sm mb-2">
          <FormattedMessage id="nextPaymentTitle" />:{' '}
          {subscription.next_payment_amount && !isSubscriptionOnCancelPeriod ? (
            <FormattedNumber
              style="currency"
              currency="EUR"
              value={subscription.next_payment_amount / 100}
              maximumFractionDigits={0}
            />
          ) : (
            '-'
          )}
        </p>

        <div className="w-full flex items-end justify-between">
          <PaymentBadge name={subscription.status.toLocaleLowerCase()} />
          <button
            onClick={() => {
              if (isSubscriptionOnCancelPeriod && !isResumePending) {
                resumeSubscription();
              } else if (subscription.product_type !== 'POS' && !isPricePending) {
                setOpenUpgradeModal(true);
              }
            }}
            disabled={subscription.product_type === 'POS'}
            type="button"
            className={classNames(
              subscription.product_type === 'POS'
                ? 'bg-white border text-black'
                : 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline-indigo-600',
              'rounded-md px-3.5 py-2.5 text-sm font-semibold  shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ',
            )}
          >
            {isPricePending || isResumePending ? (
              <Spinner
                color={subscription.product_type === 'POS' ? 'bg-primary' : 'bg-white'}
              />
            ) : (
              <FormattedMessage
                id={
                  subscription.product_type === 'POS'
                    ? 'comingSoon'
                    : isSubscriptionOnCancelPeriod
                      ? 'resume'
                      : 'manage'
                }
              />
            )}
          </button>
        </div>
      </div>
      <CancelSubscriptionModal
        open={openCancelSubscriptionModal}
        setOpen={setOpenCancelSubscriptionModal}
        date={subscription?.next_payment_date}
        subscription={subscription}
      />
      <UpgradeSubscriptionModal
        open={openUpgradeModal}
        setOpen={setOpenUpgradeModal}
        isCanceled={subscription.status.toLocaleLowerCase() === 'cancel'}
        subscription={subscription}
        setOpenCancelSubscriptionModal={setOpenCancelSubscriptionModal}
      />
    </>
  );
};
